import { useState, useEffect, useRef } from 'react';
import {
  Shield,
  Settings,
  X,
  Save,
  Info,
  Phone,
  Mail,
  MapPin,
  Bell,
  Send,
  Inbox,
  List,
  type LucideIcon,
} from 'lucide-react';
import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import { AdminCmsPlaceholderGuide } from './admin/AdminCmsPlaceholderGuide';
import type { UrgentBannerSettings } from './UrgentBanner';

interface AdminAppSettingsProps {
  currentUserId: string;
  currentUserRole: 'root' | 'admin' | 'moderator';
  onClose?: () => void;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
}

interface AdminSettings {
  allowAnonymousSignIn: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatedAt: any;
  updatedBy: string;
}

interface BrandingSettings {
  brandName?: string;
  brandTagline?: string;
  logoUrl?: string;
  metaDescription?: string;
  faviconUrl?: string;
  primaryColor?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatedAt?: any;
  updatedBy?: string;
}

export interface ContactSettings {
  contactPhone?: string;
  contactEmail?: string;
  companyAddress?: string;
  /** Email to receive Strategy Call form notifications (Brevo). */
  notificationEmail?: string;
  /** Email(s) to receive Website Service Request notifications (Brevo). */
  websiteRequestNotificationEmail?: string;
  /** Email(s) to receive ambassador application notifications (CF-6, Brevo). */
  ambassadorNotificationEmail?: string;
  /** Email(s) to receive corporate inquiry notifications (CF-7, Brevo). */
  corporateNotificationEmail?: string;
  /** Sender name for Brevo Strategy Call emails (e.g. GSGO). */
  brevoSenderName?: string;
  /** Sender email for Brevo Strategy Call emails; default noreply@gsgo.org */
  brevoSenderEmail?: string;
  /** Brevo contact list ID to add newsletter subscribers to (numeric). */
  brevoNewsletterListId?: string;
  /** Brevo CRM list ID for warrior/ambassador applications (numeric). */
  brevoAmbassadorApplicationsListId?: string;
  /** Brevo CRM list ID for corporate partners applications (numeric). */
  brevoCorporatePartnersListId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatedAt?: any;
  updatedBy?: string;
}

const DEFAULT_URGENT_BANNER_SETTINGS: UrgentBannerSettings = {
  enabled: false,
  label: 'URGENT:',
  message: '47 patients need dialysis funding this month. Your gift processes in 60 seconds.',
  ctaLabel: 'GIVE NOW →',
  ctaHref: '/donate#donationwidget',
  backgroundColor: '#C0392B',
};

type SettingsPageTab = 'security' | 'branding' | 'banner' | 'contact' | 'integrations';
type ContactFormTab = 'public' | 'recipients' | 'brevo';

const SETTINGS_PAGE_TABS: { id: SettingsPageTab; label: string; icon: LucideIcon; rootOnly?: boolean }[] = [
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'branding', label: 'Branding', icon: Settings },
  { id: 'banner', label: 'Urgent Banner', icon: Bell },
  { id: 'contact', label: 'Contact & Email', icon: Mail },
  { id: 'integrations', label: 'Brevo API', icon: Shield, rootOnly: true },
];

const CONTACT_FORM_TABS: { id: ContactFormTab; label: string; icon: LucideIcon; description: string }[] = [
  {
    id: 'public',
    label: 'Public Contact',
    icon: Phone,
    description: 'Phone, email, and address shown in the header, footer, and contact sections.',
  },
  {
    id: 'recipients',
    label: 'Recipients',
    icon: Inbox,
    description:
      'Who receives admin notifications when strategy call, website request, ambassador, or corporate forms are submitted.',
  },
  {
    id: 'brevo',
    label: 'Sender & CRM',
    icon: List,
    description: 'Brevo sender identity and CRM list IDs used by Cloud Functions and newsletter sync.',
  },
];

const DEFAULT_CONTACT_SETTINGS: ContactSettings = {
  contactPhone: '+1 832-542-6260',
  contactEmail: '',
  companyAddress: '',
  notificationEmail: '',
  websiteRequestNotificationEmail: '',
  ambassadorNotificationEmail: '',
  corporateNotificationEmail: '',
  brevoSenderName: '',
  brevoSenderEmail: 'noreply@gsgo.org',
};

/** Apple.com-inspired surfaces — System Settings rhythm, product-page typography */
const S = {
  page: 'space-y-8',
  heroTitle: 'text-[34px] font-semibold tracking-tight text-neutral-900',
  heroSubtitle: 'mt-2 max-w-2xl text-[17px] leading-relaxed text-neutral-500',
  card: 'overflow-hidden rounded-2xl bg-white shadow-[0_2px_24px_-4px_rgba(0,0,0,0.06),0_0_0_1px_rgba(0,0,0,0.04)]',
  cardPad: 'p-8',
  cardHeader: 'border-b border-neutral-100 px-8 py-6',
  panelTitle: 'text-[21px] font-semibold tracking-tight text-neutral-900',
  panelDesc: 'mt-1 text-[14px] leading-relaxed text-neutral-500',
  label: 'text-[13px] font-medium text-neutral-700',
  hint: 'mt-1.5 text-[12px] leading-snug text-neutral-400',
  input:
    'block w-full rounded-xl border-0 bg-neutral-100 px-4 py-3 text-[15px] text-neutral-900 placeholder:text-neutral-400 transition-all duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0071e3]/25',
  inputMt: 'mt-2',
  textarea:
    'block w-full resize-none rounded-xl border-0 bg-neutral-100 px-4 py-3 text-[15px] text-neutral-900 placeholder:text-neutral-400 transition-all duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0071e3]/25',
  inputIconWrap: 'relative mt-2',
  inputWithIcon: 'block w-full rounded-xl border-0 bg-neutral-100 py-3 pl-11 pr-4 text-[15px] text-neutral-900 placeholder:text-neutral-400 transition-all duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0071e3]/25',
  iconInInput: 'pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400',
  btnPrimary:
    'inline-flex items-center gap-2 rounded-full bg-[#0071e3] px-6 py-2.5 text-[14px] font-medium text-white transition-all duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)] hover:bg-[#0077ED] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/30 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:opacity-70',
  footer: 'flex flex-col gap-4 border-t border-neutral-100 bg-[#fbfbfd] px-8 py-5 sm:flex-row sm:items-center sm:justify-between',
  footerNote: 'flex items-center gap-2 text-[13px] text-neutral-500',
  segmentWrap: 'inline-flex flex-wrap gap-1 rounded-full bg-neutral-200/70 p-1.5',
  segmentActive:
    'inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2.5 text-[13px] font-medium text-neutral-900 shadow-[0_1px_4px_rgba(0,0,0,0.08)] transition-all duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)]',
  segmentIdle:
    'inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-medium text-neutral-600 transition-colors duration-200 ease-out hover:text-neutral-900',
  subTabBar: 'flex gap-6 overflow-x-auto border-b border-neutral-100 px-8',
  subTabActive:
    'inline-flex shrink-0 items-center gap-1.5 border-b-2 border-neutral-900 pb-3 pt-4 text-[14px] font-medium text-neutral-900 transition-colors duration-200',
  subTabIdle:
    'inline-flex shrink-0 items-center gap-1.5 border-b-2 border-transparent pb-3 pt-4 text-[14px] font-medium text-neutral-500 transition-colors duration-200 hover:text-neutral-700',
  tabDesc: 'border-b border-neutral-50 bg-[#fbfbfd] px-8 py-4 text-[13px] leading-relaxed text-neutral-500',
  toggleTrack:
    'relative inline-flex h-[31px] w-[51px] shrink-0 items-center rounded-full transition-colors duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/25 focus:ring-offset-2',
  toggleOn: 'bg-[#34c759]',
  toggleOff: 'bg-neutral-300',
  toggleThumb:
    'inline-block h-[27px] w-[27px] rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.15)] transition-transform duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)]',
  badgeOn: 'rounded-full bg-[#34c759]/12 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-[#248a3d]',
  badgeOff: 'rounded-full bg-neutral-200/80 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-neutral-600',
  callout: 'mb-6 flex items-start gap-3 rounded-2xl bg-neutral-100/90 px-4 py-3.5',
  calloutText: 'text-[13px] leading-relaxed text-neutral-600',
  loading: 'py-20 text-center text-[15px] text-neutral-400',
  insetGroup: 'rounded-2xl bg-neutral-50/80 p-6 ring-1 ring-black/[0.04]',
} as const;

export const AdminAppSettings = ({
  currentUserId,
  currentUserRole,
  onClose,
  showNotify,
  confirmAction,
}: AdminAppSettingsProps) => {
  const [adminSettings, setAdminSettings] = useState<AdminSettings | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [brandingSettings, setBrandingSettings] = useState<BrandingSettings | null>(null);
  const [savingBranding, setSavingBranding] = useState(false);
  const [contactSettings, setContactSettings] = useState<ContactSettings | null>(null);
  const [savingContact, setSavingContact] = useState(false);
  const [urgentBannerSettings, setUrgentBannerSettings] = useState<UrgentBannerSettings | null>(null);
  const [savingUrgentBanner, setSavingUrgentBanner] = useState(false);
  const [brevoSettings, setBrevoSettings] = useState<{ apiKey: string }>({ apiKey: '' });
  const [brevoLoaded, setBrevoLoaded] = useState(false);
  const [savingBrevo, setSavingBrevo] = useState(false);
  const [showBrevoKey, setShowBrevoKey] = useState(false);
  const [activePageTab, setActivePageTab] = useState<SettingsPageTab>('security');
  const [activeContactTab, setActiveContactTab] = useState<ContactFormTab>('public');
  const isUpdatingRef = useRef(false);
  const pendingUpdateRef = useRef<boolean | null>(null);

  // Debug: Log state changes
  useEffect(() => {
    console.log('[AdminAppSettings] State updated:', {
      allowAnonymousSignIn: adminSettings?.allowAnonymousSignIn,
      savingSettings,
      loading,
      brandingSettings,
      savingBranding,
      isUpdating: isUpdatingRef.current,
      pendingUpdate: pendingUpdateRef.current
    });
  }, [adminSettings, savingSettings, loading, brandingSettings, savingBranding]);

  // Fetch admin settings
  useEffect(() => {
    const settingsRef = doc(db, 'artifacts', appId, 'settings', 'auth');
    const unsubscribe = onSnapshot(
      settingsRef,
      (docSnap) => {
        // Skip updates while we're saving (to prevent overwriting optimistic update)
        if (isUpdatingRef.current) {
          console.log('[AdminAppSettings] Skipping Firestore update - save in progress');
          return;
        }
        
        // If we have a pending update, use that value instead of Firestore value
        if (pendingUpdateRef.current !== null) {
          console.log('[AdminAppSettings] Using pending update value:', pendingUpdateRef.current);
          const settingsData = docSnap.exists() ? docSnap.data() as AdminSettings : null;
          setAdminSettings({
            allowAnonymousSignIn: pendingUpdateRef.current,
            updatedAt: settingsData?.updatedAt || null,
            updatedBy: settingsData?.updatedBy || currentUserId,
          });
          pendingUpdateRef.current = null; // Clear pending update
          setLoading(false);
          return;
        }
        
        if (docSnap.exists()) {
          const settingsData = docSnap.data() as AdminSettings;
          console.log('[AdminAppSettings] Settings updated from Firestore:', settingsData);
          setAdminSettings(settingsData);
        } else {
          // Default settings if document doesn't exist
          const defaultSettings = {
            allowAnonymousSignIn: true, // Default to enabled
            updatedAt: serverTimestamp(),
            updatedBy: 'system',
          };
          console.log('[AdminAppSettings] No settings found, using defaults:', defaultSettings);
          setAdminSettings(defaultSettings);
        }
        setLoading(false);
      },
      (error) => {
        console.error('[AdminAppSettings] Error fetching admin settings:', error);
        // Set default settings on error
        setAdminSettings({
          allowAnonymousSignIn: true,
          updatedAt: serverTimestamp(),
          updatedBy: 'system',
        });
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [currentUserId]);

  // Fetch branding settings
  useEffect(() => {
    const brandingRef = doc(db, 'artifacts', appId, 'settings', 'branding');

    const unsubscribeBranding = onSnapshot(
      brandingRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as BrandingSettings;
          console.log('[AdminAppSettings] Branding settings updated from Firestore:', data);
          setBrandingSettings(data);
        } else {
          const defaults: BrandingSettings = {
            brandName: 'GSGO',
            brandTagline: 'Community support for CKD patients and families',
          };
          console.log(
            '[AdminAppSettings] No branding settings found, using defaults:',
            defaults
          );
          setBrandingSettings(defaults);
        }
      },
      (error) => {
        console.error('[AdminAppSettings] Error fetching branding settings:', error);
        setBrandingSettings({
          brandName: 'GSGO',
          brandTagline: 'Community support for CKD patients and families',
        });
      }
    );

    return () => unsubscribeBranding();
  }, []);

  // Fetch contact settings (phone, email, company address)
  useEffect(() => {
    const contactRef = doc(db, 'artifacts', appId, 'settings', 'contact');
    const unsubscribe = onSnapshot(
      contactRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setContactSettings(docSnap.data() as ContactSettings);
        } else {
          setContactSettings({ ...DEFAULT_CONTACT_SETTINGS });
        }
      },
      (error) => {
        console.error('[AdminAppSettings] Error fetching contact settings:', error);
        setContactSettings({ ...DEFAULT_CONTACT_SETTINGS });
      }
    );
    return () => unsubscribe();
  }, []);

  // Fetch urgent banner settings (public top notice)
  useEffect(() => {
    const urgentBannerRef = doc(db, 'artifacts', appId, 'settings', 'urgentBanner');
    const unsubscribe = onSnapshot(
      urgentBannerRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUrgentBannerSettings({
            enabled: data.enabled === true,
            label: typeof data.label === 'string' ? data.label : DEFAULT_URGENT_BANNER_SETTINGS.label,
            message:
              typeof data.message === 'string'
                ? data.message
                : DEFAULT_URGENT_BANNER_SETTINGS.message,
            ctaLabel:
              typeof data.ctaLabel === 'string'
                ? data.ctaLabel
                : DEFAULT_URGENT_BANNER_SETTINGS.ctaLabel,
            ctaHref:
              typeof data.ctaHref === 'string'
                ? data.ctaHref
                : DEFAULT_URGENT_BANNER_SETTINGS.ctaHref,
            backgroundColor:
              typeof data.backgroundColor === 'string'
                ? data.backgroundColor
                : DEFAULT_URGENT_BANNER_SETTINGS.backgroundColor,
            updatedAt: data.updatedAt ?? null,
            updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
          });
        } else {
          setUrgentBannerSettings(DEFAULT_URGENT_BANNER_SETTINGS);
        }
      },
      (error) => {
        console.error('[AdminAppSettings] Error fetching urgent banner settings:', error);
        setUrgentBannerSettings(DEFAULT_URGENT_BANNER_SETTINGS);
      }
    );
    return () => unsubscribe();
  }, []);

  // Fetch Brevo API settings (root only)
  useEffect(() => {
    if (currentUserRole !== 'root') {
      setBrevoLoaded(true);
      return;
    }
    let cancelled = false;
    const brevoRef = doc(db, 'artifacts', appId, 'settings', 'brevo');
    getDoc(brevoRef)
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists()) {
          const d = snap.data();
          setBrevoSettings({ apiKey: d?.apiKey ?? '' });
        }
        setBrevoLoaded(true);
      })
      .catch(() => setBrevoLoaded(true));
    return () => { cancelled = true; };
  }, [currentUserRole]);

  // Update anonymous sign-in setting
  const updateAnonymousSignInSetting = async (enabled: boolean) => {
    // If confirmAction is available, use it; otherwise proceed directly
    const executeUpdate = async () => {
      // Set flags first to prevent Firestore listener from overwriting
      isUpdatingRef.current = true;
      pendingUpdateRef.current = enabled;
      
      // Optimistic UI update - update state immediately for responsive feel
      const previousSettings = adminSettings;
      
      // Create a completely new object to ensure React detects the change
      const newSettings: AdminSettings = {
        allowAnonymousSignIn: enabled,
        updatedAt: previousSettings?.updatedAt || null,
        updatedBy: currentUserId,
      };
      
      console.log('[AdminAppSettings] Optimistic update:', { 
        previous: previousSettings?.allowAnonymousSignIn, 
        new: enabled,
        newSettings,
        pendingUpdate: pendingUpdateRef.current,
        willUpdateTo: newSettings.allowAnonymousSignIn
      });
      
      // Force React to re-render by creating a completely new object with explicit properties
      // Use functional update to ensure we're working with the latest state
      setAdminSettings((prev) => {
        const newState = {
          allowAnonymousSignIn: enabled,
          updatedAt: prev?.updatedAt || null,
          updatedBy: currentUserId,
        };
        console.log('[AdminAppSettings] Setting state to:', newState);
        return newState;
      });
      
      // Force a re-render by updating saving state
      setSavingSettings(true);
      
      try {
        const settingsRef = doc(db, 'artifacts', appId, 'settings', 'auth');
        await setDoc(
          settingsRef,
          {
            allowAnonymousSignIn: enabled,
            updatedAt: serverTimestamp(),
            updatedBy: currentUserId,
          },
          { merge: true }
        );
        
        console.log(`[AdminAppSettings] Successfully saved: Anonymous sign-in ${enabled ? 'enabled' : 'disabled'}`);
        
        if (showNotify) {
          showNotify(
            `Anonymous sign-in ${enabled ? 'enabled' : 'disabled'} successfully`,
            'success'
          );
        }
        
        // Allow Firestore listener to update state with server timestamp
        // Wait a bit longer to ensure the write is complete and listener has processed
        setTimeout(() => {
          isUpdatingRef.current = false;
          // Keep pendingUpdateRef until listener confirms the update
        }, 1000);
      } catch (error) {
        console.error('[AdminAppSettings] Error updating settings:', error);
        isUpdatingRef.current = false;
        pendingUpdateRef.current = null;
        
        // Revert optimistic update on error
        if (previousSettings) {
          setAdminSettings(previousSettings);
        }
        
        if (showNotify) {
          showNotify('Failed to update settings. Please try again.', 'error');
        } else {
          alert(`Failed to update settings: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      } finally {
        setSavingSettings(false);
      }
    };

    // Use confirmation dialog if available, otherwise proceed directly
    if (confirmAction && showNotify) {
      confirmAction(
        enabled ? 'Enable Anonymous Sign-In?' : 'Disable Anonymous Sign-In?',
        enabled
          ? 'This will allow users to access the app without signing in with Google or Email. They will be able to submit reports anonymously.'
          : 'This will require all users to sign in with Google or Email before accessing the app. Existing anonymous users will be signed out.',
        executeUpdate
      );
    } else {
      // Proceed without confirmation if confirmAction is not available
      executeUpdate();
    }
  };

  const isModalMode = !!onClose;

  // Compute toggle state explicitly - handle null/undefined properly
  // This ensures consistent state reading throughout the component
  const isAnonymousSignInEnabled = adminSettings 
    ? (adminSettings.allowAnonymousSignIn === true) 
    : true; // Default to enabled if settings not loaded

  const settingsContent = (
    <div className={`${isModalMode ? 'overflow-y-auto bg-[#f5f5f7] p-6' : ''}`}>
      {loading ? (
        <div className={S.loading}>Loading settings…</div>
      ) : (
        <div className={S.page}>
          {/* Page Header */}
          {!isModalMode && (
            <header className="pt-2">
              <h1 className={S.heroTitle}>Settings</h1>
              <p className={S.heroSubtitle}>
                Manage security, branding, notifications, and integrations for your site.
              </p>
            </header>
          )}

          {/* Segmented control — apple.com product nav rhythm */}
          <nav aria-label="Settings sections" className={S.segmentWrap}>
            {SETTINGS_PAGE_TABS.filter(
              (tab) => !tab.rootOnly || currentUserRole === 'root'
            ).map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activePageTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActivePageTab(tab.id)}
                  className={isActive ? S.segmentActive : S.segmentIdle}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <TabIcon size={15} className={isActive ? 'text-neutral-900' : 'text-neutral-400'} />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          {/* Authentication Settings */}
          {activePageTab === 'security' && (
          <section className={S.card} aria-labelledby="settings-security-title">
            <div className={S.cardHeader}>
              <h2 id="settings-security-title" className={S.panelTitle}>
                Security
              </h2>
              <p className={S.panelDesc}>Control how visitors sign in and access the app.</p>
            </div>
            <div className={S.cardPad}>
            
            {/* Anonymous Sign-In — iOS-style row */}
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-8">
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <h3 className="text-[17px] font-semibold text-neutral-900">Anonymous sign-in</h3>
                    <span className={isAnonymousSignInEnabled ? S.badgeOn : S.badgeOff}>
                      {isAnonymousSignInEnabled ? 'On' : 'Off'}
                    </span>
                  </div>
                  <p className="text-[15px] leading-relaxed text-neutral-500">
                    Let visitors browse and submit reports without a Google or email account.
                  </p>
                  <div className={`${S.callout} mt-5`}>
                    <Info className="mt-0.5 shrink-0 text-neutral-400" size={16} />
                    <div className={S.calloutText}>
                      <p className="mb-2 font-medium text-neutral-700">When {isAnonymousSignInEnabled ? 'enabled' : 'disabled'}</p>
                      <ul className="list-inside list-disc space-y-1 text-neutral-500">
                        {isAnonymousSignInEnabled ? (
                          <>
                            <li>Immediate access without authentication</li>
                            <li>Anonymous report submissions</li>
                            <li>Users can upgrade to a full account later</li>
                          </>
                        ) : (
                          <>
                            <li>Google or email sign-in required</li>
                            <li>Existing anonymous sessions end</li>
                            <li>Stronger accountability and tracking</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!adminSettings || savingSettings || loading) {
                      console.warn('[AdminAppSettings] Toggle disabled:', { 
                        adminSettings: !!adminSettings, 
                        savingSettings, 
                        loading 
                      });
                      return;
                    }
                    const currentValue = isAnonymousSignInEnabled;
                    const newValue = !currentValue;
                    console.log('[AdminAppSettings] Toggle clicked:', { 
                      current: currentValue, 
                      new: newValue,
                      adminSettingsValue: adminSettings.allowAnonymousSignIn,
                      computedValue: isAnonymousSignInEnabled
                    });
                    updateAnonymousSignInSetting(newValue);
                  }}
                  disabled={savingSettings || loading || !adminSettings}
                  className={`${S.toggleTrack} ${
                    isAnonymousSignInEnabled ? S.toggleOn : S.toggleOff
                  } ${savingSettings || loading || !adminSettings ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  aria-label={`${isAnonymousSignInEnabled ? 'Disable' : 'Enable'} anonymous sign-in`}
                  type="button"
                >
                  <span
                    className={`${S.toggleThumb} ${
                      isAnonymousSignInEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              
              {savingSettings && (
                <div className="flex items-center gap-2 text-[14px] text-[#0071e3]">
                  <Save size={16} className="animate-spin" />
                  <span>Saving…</span>
                </div>
              )}

              {adminSettings?.updatedBy && adminSettings.updatedBy !== 'system' && (
                <p className="border-t border-neutral-100 pt-4 text-[12px] text-neutral-400">
                  Last updated{' '}
                  {adminSettings.updatedAt?.toDate
                    ? adminSettings.updatedAt.toDate().toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'Unknown date'}
                </p>
              )}
            </div>
            </div>
          </section>
          )}

          {/* Branding Settings Section */}
          {activePageTab === 'branding' && (
          <section className={S.card} aria-labelledby="settings-branding-title">
            <div className={S.cardHeader}>
              <h2 id="settings-branding-title" className={S.panelTitle}>Branding</h2>
              <p className={S.panelDesc}>
                Logo, tagline, and metadata shown across the public site and in search results.
              </p>
            </div>

            <form
              className={`${S.cardPad} space-y-6`}
              onSubmit={async (e) => {
                e.preventDefault();
                if (!brandingSettings || savingBranding) return;

                try {
                  setSavingBranding(true);
                  const brandingRef = doc(db, 'artifacts', appId, 'settings', 'branding');
                  await setDoc(
                    brandingRef,
                    {
                      brandName: brandingSettings.brandName || 'GSGO',
                      brandTagline: brandingSettings.brandTagline || 'Community support for CKD patients and families',
                      logoUrl: brandingSettings.logoUrl || '',
                      metaDescription: brandingSettings.metaDescription || '',
                      faviconUrl: brandingSettings.faviconUrl || '',
                      primaryColor: brandingSettings.primaryColor || '',
                      updatedAt: serverTimestamp(),
                      updatedBy: currentUserId,
                    },
                    { merge: true }
                  );

                  if (showNotify) {
                    showNotify('Branding settings updated successfully', 'success');
                  }
                } catch (error) {
                  console.error('[AdminAppSettings] Error saving branding settings:', error);
                  if (showNotify) {
                    showNotify(
                      'Failed to update branding settings. Please try again.',
                      'error'
                    );
                  } else {
                    alert(
                      `Failed to update branding settings: ${
                        error instanceof Error ? error.message : 'Unknown error'
                      }`
                    );
                  }
                } finally {
                  setSavingBranding(false);
                }
              }}
            >
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="brandName" className={S.label}>Brand name</label>
                  <input
                    id="brandName"
                    type="text"
                    className={`${S.inputMt} ${S.input}`}
                    placeholder="GSGO"
                    value={brandingSettings?.brandName ?? ''}
                    onChange={(e) =>
                      setBrandingSettings((prev) => ({
                        ...(prev || {}),
                        brandName: e.target.value,
                      }))
                    }
                  />
                  <p className={S.hint}>Header, footer, and browser title.</p>
                </div>

                <div>
                  <label htmlFor="brandTagline" className={S.label}>Tagline</label>
                  <input
                    id="brandTagline"
                    type="text"
                    className={`${S.inputMt} ${S.input}`}
                    placeholder="Citizen Reporting & Project Verification"
                    value={brandingSettings?.brandTagline ?? ''}
                    onChange={(e) =>
                      setBrandingSettings((prev) => ({
                        ...(prev || {}),
                        brandTagline: e.target.value,
                      }))
                    }
                  />
                  <p className={S.hint}>Shown under the brand name and in the footer.</p>
                </div>
              </div>

              <div>
                <label htmlFor="logoUrl" className={S.label}>Logo URL <span className="font-normal text-neutral-400">(optional)</span></label>
                <input
                  id="logoUrl"
                  type="url"
                  className={`${S.inputMt} ${S.input}`}
                  placeholder="https://example.com/logo.png"
                  value={brandingSettings?.logoUrl ?? ''}
                  onChange={(e) =>
                    setBrandingSettings((prev) => ({
                      ...(prev || {}),
                      logoUrl: e.target.value,
                    }))
                  }
                />
                <p className={S.hint}>Custom logo image for future header components.</p>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="faviconUrl" className={S.label}>Favicon URL</label>
                <input
                  type="url"
                  id="faviconUrl"
                  className={`${S.inputMt} ${S.input}`}
                  placeholder="https://..."
                  value={brandingSettings?.faviconUrl ?? ''}
                  onChange={(e) =>
                    setBrandingSettings((prev) => ({ ...(prev || {}), faviconUrl: e.target.value }))
                  }
                />
              </div>
              <div>
                <label htmlFor="primaryColor" className={S.label}>Primary color</label>
                <input
                  type="text"
                  id="primaryColor"
                  className={`${S.inputMt} ${S.input}`}
                  placeholder="#2563EB"
                  value={brandingSettings?.primaryColor ?? ''}
                  onChange={(e) =>
                    setBrandingSettings((prev) => ({ ...(prev || {}), primaryColor: e.target.value }))
                  }
                />
                <p className={S.hint}>Hex value, e.g. #2563EB</p>
              </div>
              </div>

              <div className={S.footer}>
                <p className={S.footerNote}>
                  <Info size={14} className="shrink-0 text-neutral-400" />
                  Changes apply immediately on the public site.
                </p>
                <button
                  type="submit"
                  disabled={savingBranding || !brandingSettings}
                  className={S.btnPrimary}
                >
                  <Save size={16} className={savingBranding ? 'animate-spin' : ''} />
                  {savingBranding ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </section>
          )}

          {/* Urgent Site Banner */}
          {activePageTab === 'banner' && (
          <section className={S.card} aria-labelledby="settings-banner-title">
            <div className={S.cardHeader}>
              <h2 id="settings-banner-title" className={S.panelTitle}>Urgent banner</h2>
              <p className={S.panelDesc}>
                A top-of-page notice for time-sensitive calls to action on public pages.
              </p>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!urgentBannerSettings || savingUrgentBanner) return;
                try {
                  setSavingUrgentBanner(true);
                  const urgentBannerRef = doc(db, 'artifacts', appId, 'settings', 'urgentBanner');
                  await setDoc(
                    urgentBannerRef,
                    {
                      enabled: urgentBannerSettings.enabled === true,
                      label:
                        (urgentBannerSettings.label || DEFAULT_URGENT_BANNER_SETTINGS.label).trim(),
                      message:
                        (urgentBannerSettings.message ||
                          DEFAULT_URGENT_BANNER_SETTINGS.message).trim(),
                      ctaLabel:
                        (urgentBannerSettings.ctaLabel ||
                          DEFAULT_URGENT_BANNER_SETTINGS.ctaLabel).trim(),
                      ctaHref:
                        (urgentBannerSettings.ctaHref ||
                          DEFAULT_URGENT_BANNER_SETTINGS.ctaHref).trim(),
                      backgroundColor:
                        (urgentBannerSettings.backgroundColor ||
                          DEFAULT_URGENT_BANNER_SETTINGS.backgroundColor).trim(),
                      updatedAt: serverTimestamp(),
                      updatedBy: currentUserId,
                    },
                    { merge: true }
                  );
                  if (showNotify) showNotify('Urgent banner settings updated successfully', 'success');
                } catch (error) {
                  console.error('[AdminAppSettings] Error saving urgent banner settings:', error);
                  if (showNotify) {
                    showNotify('Failed to update urgent banner settings. Please try again.', 'error');
                  } else {
                    alert(
                      `Failed to update urgent banner settings: ${
                        error instanceof Error ? error.message : 'Unknown error'
                      }`
                    );
                  }
                } finally {
                  setSavingUrgentBanner(false);
                }
              }}
            >
              <div className={`${S.cardPad} space-y-6`}>
                <div className="flex items-center justify-between gap-8 rounded-2xl bg-neutral-50/80 px-5 py-4 ring-1 ring-black/[0.04]">
                  <div>
                    <p className="text-[17px] font-semibold text-neutral-900">Show urgent notice</p>
                    <p className="mt-0.5 text-[14px] text-neutral-500">
                      Visible on all public pages. Hidden in admin.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setUrgentBannerSettings((prev) => ({
                        ...(prev || DEFAULT_URGENT_BANNER_SETTINGS),
                        enabled: !(prev?.enabled === true),
                      }))
                    }
                    className={`${S.toggleTrack} ${
                      urgentBannerSettings?.enabled ? 'bg-[#ff3b30]' : S.toggleOff
                    }`}
                    aria-label={`${
                      urgentBannerSettings?.enabled ? 'Disable' : 'Enable'
                    } urgent site banner`}
                  >
                    <span
                      className={`${S.toggleThumb} ${
                        urgentBannerSettings?.enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label htmlFor="urgentLabel" className={S.label}>Label</label>
                    <input
                      id="urgentLabel"
                      type="text"
                      value={urgentBannerSettings?.label ?? ''}
                      onChange={(e) =>
                        setUrgentBannerSettings((prev) => ({
                          ...(prev || DEFAULT_URGENT_BANNER_SETTINGS),
                          label: e.target.value,
                        }))
                      }
                      className={`${S.inputMt} ${S.input}`}
                      placeholder="URGENT:"
                    />
                  </div>

                  <div>
                    <label htmlFor="urgentBackgroundColor" className={S.label}>Background</label>
                    <div className={`${S.inputMt} flex items-center gap-3`}>
                      <input
                        id="urgentBackgroundColorPicker"
                        type="color"
                        value={urgentBannerSettings?.backgroundColor ?? DEFAULT_URGENT_BANNER_SETTINGS.backgroundColor}
                        onChange={(e) =>
                          setUrgentBannerSettings((prev) => ({
                            ...(prev || DEFAULT_URGENT_BANNER_SETTINGS),
                            backgroundColor: e.target.value,
                          }))
                        }
                        className="h-11 w-14 cursor-pointer rounded-xl border-0 bg-neutral-100 p-1"
                        aria-label="Pick urgent banner background color"
                      />
                      <input
                        id="urgentBackgroundColor"
                        type="text"
                        value={urgentBannerSettings?.backgroundColor ?? ''}
                        onChange={(e) =>
                          setUrgentBannerSettings((prev) => ({
                            ...(prev || DEFAULT_URGENT_BANNER_SETTINGS),
                            backgroundColor: e.target.value,
                          }))
                        }
                        className={S.input}
                        placeholder="#C0392B"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="urgentMessage" className={S.label}>Message</label>
                  <input
                    id="urgentMessage"
                    type="text"
                    value={urgentBannerSettings?.message ?? ''}
                    onChange={(e) =>
                      setUrgentBannerSettings((prev) => ({
                        ...(prev || DEFAULT_URGENT_BANNER_SETTINGS),
                        message: e.target.value,
                      }))
                    }
                    className={`${S.inputMt} ${S.input}`}
                    placeholder="47 patients need dialysis funding this month. Your gift processes in 60 seconds."
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label htmlFor="urgentCtaLabel" className={S.label}>Button label</label>
                    <input
                      id="urgentCtaLabel"
                      type="text"
                      value={urgentBannerSettings?.ctaLabel ?? ''}
                      onChange={(e) =>
                        setUrgentBannerSettings((prev) => ({
                          ...(prev || DEFAULT_URGENT_BANNER_SETTINGS),
                          ctaLabel: e.target.value,
                        }))
                      }
                      className={`${S.inputMt} ${S.input}`}
                      placeholder="GIVE NOW →"
                    />
                  </div>
                  <div>
                    <label htmlFor="urgentCtaHref" className={S.label}>Button link</label>
                    <input
                      id="urgentCtaHref"
                      type="text"
                      value={urgentBannerSettings?.ctaHref ?? ''}
                      onChange={(e) =>
                        setUrgentBannerSettings((prev) => ({
                          ...(prev || DEFAULT_URGENT_BANNER_SETTINGS),
                          ctaHref: e.target.value,
                        }))
                      }
                      className={`${S.inputMt} ${S.input}`}
                      placeholder="/donate#donationwidget"
                    />
                  </div>
                </div>

                <p className={S.hint}>
                  Use a relative path for internal pages or a full https:// URL for external links.
                </p>
              </div>

              <div className={S.footer}>
                <span className={S.footerNote} />
                <button
                  type="submit"
                  disabled={savingUrgentBanner || !urgentBannerSettings}
                  className={S.btnPrimary}
                >
                  <Save size={15} className={savingUrgentBanner ? 'animate-spin' : ''} />
                  {savingUrgentBanner ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </section>
          )}

          {/* Contact & Company Information */}
          {activePageTab === 'contact' && (
          <section className={S.card} aria-labelledby="settings-contact-title">
            <div className={S.cardHeader}>
              <h2 id="settings-contact-title" className={S.panelTitle}>Contact &amp; email</h2>
              <p className={S.panelDesc}>
                Public contact details, notification recipients, and Brevo delivery settings.
              </p>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!contactSettings || savingContact) return;
                try {
                  setSavingContact(true);
                  const contactRef = doc(db, 'artifacts', appId, 'settings', 'contact');
                  await setDoc(
                    contactRef,
                    {
                      contactPhone: (contactSettings.contactPhone ?? '').trim() || null,
                      contactEmail: (contactSettings.contactEmail ?? '').trim() || null,
                      companyAddress: (contactSettings.companyAddress ?? '').trim() || null,
                      notificationEmail: (contactSettings.notificationEmail ?? '').trim() || null,
                      websiteRequestNotificationEmail: (contactSettings.websiteRequestNotificationEmail ?? '').trim() || null,
                      ambassadorNotificationEmail: (contactSettings.ambassadorNotificationEmail ?? '').trim() || null,
                      corporateNotificationEmail: (contactSettings.corporateNotificationEmail ?? '').trim() || null,
                      brevoSenderName: (contactSettings.brevoSenderName ?? '').trim() || null,
                      brevoSenderEmail: (contactSettings.brevoSenderEmail ?? '').trim() || null,
                      brevoNewsletterListId: (String(contactSettings.brevoNewsletterListId ?? '').trim()) ? parseInt(String(contactSettings.brevoNewsletterListId), 10) : null,
                      brevoAmbassadorApplicationsListId: (String(contactSettings.brevoAmbassadorApplicationsListId ?? '').trim())
                        ? parseInt(String(contactSettings.brevoAmbassadorApplicationsListId), 10)
                        : null,
                      brevoCorporatePartnersListId: (String(contactSettings.brevoCorporatePartnersListId ?? '').trim())
                        ? parseInt(String(contactSettings.brevoCorporatePartnersListId), 10)
                        : null,
                      updatedAt: serverTimestamp(),
                      updatedBy: currentUserId,
                    },
                    { merge: true }
                  );
                  if (showNotify) {
                    showNotify('Contact information updated successfully', 'success');
                  }
                } catch (error) {
                  console.error('[AdminAppSettings] Error saving contact settings:', error);
                  if (showNotify) {
                    showNotify('Failed to update contact information. Please try again.', 'error');
                  } else {
                    alert(`Failed to update contact information: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  }
                } finally {
                  setSavingContact(false);
                }
              }}
            >
              {/* Contact sub-tabs — macOS Settings–style underline nav */}
              <div className={S.subTabBar} role="tablist" aria-label="Contact settings">
                {CONTACT_FORM_TABS.map((tab) => {
                  const TabIcon = tab.icon;
                  const isActive = activeContactTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveContactTab(tab.id)}
                      className={isActive ? S.subTabActive : S.subTabIdle}
                    >
                      <TabIcon size={14} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              <p className={S.tabDesc}>
                {CONTACT_FORM_TABS.find((t) => t.id === activeContactTab)?.description}
              </p>

              {/* ── Public contact ── */}
              {activeContactTab === 'public' && (
              <div className="px-8 pb-8 pt-6">
                <AdminCmsPlaceholderGuide
                  title="Embed codes for CMS content"
                  context="Use these in CMS page body or custom header/footer HTML; they are replaced with the values below."
                  className="mb-4"
                />

                <div className="mb-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Phone */}
                  <div>
                    <label htmlFor="contactPhone" className={S.label}>
                      Phone number
                    </label>
                    <div className={S.inputIconWrap}>
                      <Phone size={14} className={S.iconInInput} />
                      <input
                        id="contactPhone"
                        type="tel"
                        className={S.inputWithIcon}
                        placeholder="+1 832-542-6260"
                        value={contactSettings?.contactPhone ?? ''}
                        onChange={(e) =>
                          setContactSettings((prev) => ({
                          ...(prev || { contactPhone: '', contactEmail: '', companyAddress: '', notificationEmail: '', websiteRequestNotificationEmail: '', ambassadorNotificationEmail: '', brevoSenderName: '', brevoSenderEmail: 'noreply@gsgo.org' }),
                      contactPhone: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <p className={S.hint}>E.164 or readable format, e.g. +1 832-542-6260</p>
                  </div>

                  {/* Contact email */}
                  <div>
                    <label htmlFor="contactEmail" className={S.label}>
                      Contact email
                    </label>
                    <div className={S.inputIconWrap}>
                      <Mail size={14} className={S.iconInInput} />
                      <input
                        id="contactEmail"
                        type="email"
                        className={S.inputWithIcon}
                        placeholder="contact@gsgo.org"
                        value={contactSettings?.contactEmail ?? ''}
                        onChange={(e) =>
                          setContactSettings((prev) => ({
                          ...(prev || { contactPhone: '', contactEmail: '', companyAddress: '', notificationEmail: '', websiteRequestNotificationEmail: '', ambassadorNotificationEmail: '', brevoSenderName: '', brevoSenderEmail: 'noreply@gsgo.org' }),
                      contactEmail: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <p className={S.hint}>Support and inquiry email address</p>
                  </div>
                </div>

                {/* Company address */}
                <div>
                  <label htmlFor="companyAddress" className={`${S.label} flex items-center gap-1.5`}>
                    <MapPin size={13} className="text-neutral-400" />
                    Company address
                  </label>
                  <textarea
                    id="companyAddress"
                    rows={2}
                    className={`${S.inputMt} ${S.textarea}`}
                    placeholder="123 Main St, Suite 100&#10;City, State ZIP"
                    value={contactSettings?.companyAddress ?? ''}
                    onChange={(e) =>
                      setContactSettings((prev) => ({
...(prev || { contactPhone: '', contactEmail: '', companyAddress: '', notificationEmail: '', websiteRequestNotificationEmail: '', ambassadorNotificationEmail: '', brevoSenderName: '', brevoSenderEmail: 'noreply@gsgo.org' }),
                      companyAddress: e.target.value,
                      }))
                    }
                  />
                  <p className={S.hint}>Physical address shown in footer or contact sections.</p>
                </div>
              </div>
              )}

              {/* ── Form notification recipients ── */}
              {activeContactTab === 'recipients' && (
              <div className="px-8 pb-8 pt-6">
                <div className={S.callout}>
                  <Bell size={16} className="mt-0.5 shrink-0 text-neutral-400" />
                  <p className={S.calloutText}>
                    Submissions are stored in Firestore and delivered by Cloud Functions via Brevo.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="notificationEmail" className={S.label}>
                      Strategy Call recipients
                    </label>
                    <textarea
                      id="notificationEmail"
                      rows={2}
                      className={`${S.inputMt} ${S.textarea}`}
                      placeholder="admin@gsgo.org&#10;operations@gsgo.org"
                      value={contactSettings?.notificationEmail ?? ''}
                      onChange={(e) =>
                        setContactSettings((prev) => ({
                          ...(prev || { contactPhone: '', contactEmail: '', companyAddress: '', notificationEmail: '', websiteRequestNotificationEmail: '', ambassadorNotificationEmail: '', brevoSenderName: '', brevoSenderEmail: 'noreply@gsgo.org' }),
                          notificationEmail: e.target.value,
                        }))
                      }
                    />
                    <p className={S.hint}>Notified when someone submits the Book a Strategy Call form.</p>
                  </div>
                  <div>
                    <label htmlFor="websiteRequestNotificationEmail" className={S.label}>
                      Website Request recipients
                    </label>
                    <textarea
                      id="websiteRequestNotificationEmail"
                      rows={2}
                      className={`${S.inputMt} ${S.textarea}`}
                      placeholder="ops@gsgo.org"
                      value={contactSettings?.websiteRequestNotificationEmail ?? ''}
                      onChange={(e) =>
                        setContactSettings((prev) => ({
                          ...(prev || { contactPhone: '', contactEmail: '', companyAddress: '', notificationEmail: '', websiteRequestNotificationEmail: '', ambassadorNotificationEmail: '', brevoSenderName: '', brevoSenderEmail: 'noreply@gsgo.org' }),
                          websiteRequestNotificationEmail: e.target.value,
                        }))
                      }
                    />
                    <p className={S.hint}>Notified when a customer submits a Website Development &amp; Enhancement Request.</p>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="ambassadorNotificationEmail" className={S.label}>
                      Ambassador application recipients
                    </label>
                    <textarea
                      id="ambassadorNotificationEmail"
                      rows={2}
                      className={`${S.inputMt} ${S.textarea}`}
                      placeholder="ambassadors@gsgo.org"
                      value={contactSettings?.ambassadorNotificationEmail ?? ''}
                      onChange={(e) =>
                        setContactSettings((prev) => ({
                          ...(prev || { contactPhone: '', contactEmail: '', companyAddress: '', notificationEmail: '', websiteRequestNotificationEmail: '', ambassadorNotificationEmail: '', brevoSenderName: '', brevoSenderEmail: 'noreply@gsgo.org' }),
                          ambassadorNotificationEmail: e.target.value,
                        }))
                      }
                    />
                    <p className={S.hint}>Used by CF-6. Default fallback is ambassadors@gsgo.org when this field is blank.</p>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="corporateNotificationEmail" className={S.label}>
                      Corporate inquiry recipients
                    </label>
                    <textarea
                      id="corporateNotificationEmail"
                      rows={2}
                      className={`${S.inputMt} ${S.textarea}`}
                      placeholder="admin@gsgo.org"
                      value={contactSettings?.corporateNotificationEmail ?? ''}
                      onChange={(e) =>
                        setContactSettings((prev) => ({
                          ...(prev || {
                            contactPhone: '',
                            contactEmail: '',
                            companyAddress: '',
                            notificationEmail: '',
                            websiteRequestNotificationEmail: '',
                            ambassadorNotificationEmail: '',
                            corporateNotificationEmail: '',
                            brevoSenderName: '',
                            brevoSenderEmail: 'noreply@gsgo.org',
                          }),
                          corporateNotificationEmail: e.target.value,
                        }))
                      }
                    />
                    <p className={S.hint}>
                      Used by CF-7 when a corporate partnership form is submitted. Default fallback is
                      admin@gsgo.org when this field is blank.
                    </p>
                  </div>
                </div>
              </div>
              )}

              {/* ── Brevo sender & CRM lists ── */}
              {activeContactTab === 'brevo' && (
              <div className="px-8 pb-8 pt-6">
                <div className={S.insetGroup}>
                  <div className="mb-5 flex items-center gap-2">
                    <Send size={15} className="text-neutral-500" />
                    <span className="text-[15px] font-semibold text-neutral-900">Sender identity</span>
                  </div>
                  <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="brevoSenderName" className={S.label}>
                        Sender name
                      </label>
                      <input
                        id="brevoSenderName"
                        type="text"
                        className={`${S.inputMt} ${S.input}`}
                        placeholder="GSGO"
                        value={contactSettings?.brevoSenderName ?? ''}
                        onChange={(e) =>
                          setContactSettings((prev) => ({
...(prev || { contactPhone: '', contactEmail: '', companyAddress: '', notificationEmail: '', websiteRequestNotificationEmail: '', ambassadorNotificationEmail: '', brevoSenderName: '', brevoSenderEmail: 'noreply@gsgo.org' }),
                        brevoSenderName: e.target.value,
                          }))
                        }
                      />
                      <p className={S.hint}>Appears as the "From" name. Default: GSGO.</p>
                    </div>
                    <div>
                      <label htmlFor="brevoSenderEmail" className={S.label}>
                        Sender email
                      </label>
                      <div className={S.inputIconWrap}>
                        <Mail size={14} className={S.iconInInput} />
                        <input
                          id="brevoSenderEmail"
                          type="email"
                          className={S.inputWithIcon}
                          placeholder="noreply@gsgo.org"
                          value={contactSettings?.brevoSenderEmail ?? ''}
                          onChange={(e) =>
                            setContactSettings((prev) => ({
...(prev || { contactPhone: '', contactEmail: '', companyAddress: '', notificationEmail: '', websiteRequestNotificationEmail: '', ambassadorNotificationEmail: '', brevoSenderName: '', brevoSenderEmail: 'noreply@gsgo.org' }),
                        brevoSenderEmail: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <p className={S.hint}>Default: noreply@gsgo.org. Must be a verified sender in Brevo.</p>
                    </div>
                  </div>

                  <div className="mb-4 flex items-center gap-2 border-t border-neutral-200/60 pt-6">
                    <List size={15} className="text-neutral-500" />
                    <span className="text-[15px] font-semibold text-neutral-900">CRM lists</span>
                  </div>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <label htmlFor="brevoNewsletterListId" className={S.label}>
                        Newsletter list ID
                      </label>
                      <input
                        id="brevoNewsletterListId"
                        type="number"
                        min={1}
                        step={1}
                        className={`${S.inputMt} ${S.input}`}
                        placeholder="e.g. 5"
                        value={contactSettings?.brevoNewsletterListId != null ? String(contactSettings.brevoNewsletterListId) : ''}
                        onChange={(e) =>
                          setContactSettings((prev) => ({
                            ...(prev || { contactPhone: '', contactEmail: '', companyAddress: '', notificationEmail: '', websiteRequestNotificationEmail: '', ambassadorNotificationEmail: '', brevoSenderName: '', brevoSenderEmail: 'noreply@gsgo.org' }),
                            brevoNewsletterListId: e.target.value,
                          }))
                        }
                      />
                      <p className={S.hint}>Used when you run &quot;Sync to Brevo&quot; in Newsletter Subscribers. Set the API key under the Brevo API tab.</p>
                    </div>
                    <div>
                      <label htmlFor="brevoAmbassadorApplicationsListId" className={S.label}>
                        Warrior applications list ID
                      </label>
                      <input
                        id="brevoAmbassadorApplicationsListId"
                        type="number"
                        min={1}
                        step={1}
                        className={`${S.inputMt} ${S.input}`}
                        placeholder="e.g. 3"
                        value={
                          contactSettings?.brevoAmbassadorApplicationsListId != null
                            ? String(contactSettings.brevoAmbassadorApplicationsListId)
                            : ''
                        }
                        onChange={(e) =>
                          setContactSettings((prev) => ({
                            ...(prev || {
                              contactPhone: '',
                              contactEmail: '',
                              companyAddress: '',
                              notificationEmail: '',
                              websiteRequestNotificationEmail: '',
                              ambassadorNotificationEmail: '',
                              brevoSenderName: '',
                              brevoSenderEmail: 'noreply@gsgo.org',
                            }),
                            brevoAmbassadorApplicationsListId: e.target.value,
                          }))
                        }
                      />
                      <p className={S.hint}>
                        In Brevo CRM → Lists, create a list (e.g. &quot;Warrior Applications&quot;) and paste its numeric ID here.
                        CF-6 adds each applicant to this list automatically.
                      </p>
                    </div>
                    <div>
                      <label htmlFor="brevoCorporatePartnersListId" className={S.label}>
                        Corporate Partners list ID
                      </label>
                      <input
                        id="brevoCorporatePartnersListId"
                        type="number"
                        min={1}
                        step={1}
                        className={`${S.inputMt} ${S.input}`}
                        placeholder="e.g. 3"
                        value={
                          contactSettings?.brevoCorporatePartnersListId != null
                            ? String(contactSettings.brevoCorporatePartnersListId)
                            : ''
                        }
                        onChange={(e) =>
                          setContactSettings((prev) => ({
                            ...(prev || {
                              contactPhone: '',
                              contactEmail: '',
                              companyAddress: '',
                              notificationEmail: '',
                              websiteRequestNotificationEmail: '',
                              ambassadorNotificationEmail: '',
                              corporateNotificationEmail: '',
                              brevoSenderName: '',
                              brevoSenderEmail: 'noreply@gsgo.org',
                            }),
                            brevoCorporatePartnersListId: e.target.value,
                          }))
                        }
                      />
                      <p className={S.hint}>
                        Brevo CRM → Lists → #3 Corporate Partners Applications. CF-7 adds each inquiry
                        contact to this list automatically.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* ── Footer / Save ── */}
              <div className={S.footer}>
                <p className={S.footerNote}>
                  <Info size={14} className="shrink-0 text-neutral-400" />
                  One save updates all contact and email settings site-wide.
                </p>
                <button type="submit" disabled={savingContact || !contactSettings} className={S.btnPrimary}>
                  <Save size={15} className={savingContact ? 'animate-spin' : ''} />
                  {savingContact ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </section>
          )}

          {/* Brevo API (Root only) */}
          {activePageTab === 'integrations' && currentUserRole === 'root' && (
          <section className={S.card} aria-labelledby="settings-brevo-title">
              <div className={S.cardHeader}>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 id="settings-brevo-title" className={S.panelTitle}>Brevo API</h2>
                  <span className="rounded-full bg-neutral-200/80 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-neutral-600">
                    Root only
                  </span>
                </div>
                <p className={S.panelDesc}>
                  API key for newsletter sync. Set list IDs under Contact &amp; email → Sender &amp; CRM.
                </p>
              </div>
              {!brevoLoaded ? (
                <div className={`${S.cardPad} text-center text-[15px] text-neutral-400`}>Loading…</div>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (savingBrevo) return;
                    setSavingBrevo(true);
                    try {
                      const brevoRef = doc(db, 'artifacts', appId, 'settings', 'brevo');
                      await setDoc(brevoRef, {
                        apiKey: (brevoSettings.apiKey ?? '').trim() || null,
                        updatedAt: serverTimestamp(),
                        updatedBy: currentUserId,
                      }, { merge: true });
                      if (showNotify) showNotify('Brevo API key saved', 'success');
                    } catch (err) {
                      console.error('[AdminAppSettings] Error saving Brevo settings:', err);
                      if (showNotify) showNotify('Failed to save Brevo API key', 'error');
                    } finally {
                      setSavingBrevo(false);
                    }
                  }}
                >
                  <div className={`${S.cardPad} space-y-2`}>
                    <label htmlFor="brevoApiKey" className={S.label}>API key</label>
                    <div className="relative mt-2">
                      <input
                        id="brevoApiKey"
                        type={showBrevoKey ? 'text' : 'password'}
                        className={`${S.input} pr-16`}
                        placeholder="xkeysib-…"
                        value={brevoSettings.apiKey ?? ''}
                        onChange={(e) => setBrevoSettings((prev) => ({ ...prev, apiKey: e.target.value }))}
                      />
                      <button
                        type="button"
                        onClick={() => setShowBrevoKey((v) => !v)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-medium text-[#0071e3] transition-colors hover:text-[#0077ED]"
                      >
                        {showBrevoKey ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <p className={S.hint}>Brevo → Settings → SMTP &amp; API → API Keys (Contacts permission).</p>
                  </div>
                  <div className={S.footer}>
                    <span className={S.footerNote} />
                    <button type="submit" disabled={savingBrevo} className={S.btnPrimary}>
                      <Save size={15} className={savingBrevo ? 'animate-spin' : ''} />
                      {savingBrevo ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </form>
              )}
            </section>
          )}

          {activePageTab === 'integrations' && currentUserRole !== 'root' && (
            <div className={`${S.card} ${S.cardPad} text-[15px] text-neutral-600`}>
              Brevo API settings are available to root administrators only.
            </div>
          )}

        </div>
      )}
    </div>
  );

  // If modal mode, wrap in modal container
  if (isModalMode) {
    return (
      <>
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm">
          <div className="my-8 flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-[#f5f5f7] shadow-[0_24px_80px_rgba(0,0,0,0.18)]">
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-neutral-200/80 bg-white/80 px-6 py-5 backdrop-blur-xl">
              <h2 className="text-[22px] font-semibold tracking-tight text-neutral-900">Settings</h2>
              <button
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-200/80 text-neutral-600 transition-colors duration-200 hover:bg-neutral-300/80 hover:text-neutral-900"
                aria-label="Close settings"
              >
                <X size={18} />
              </button>
            </div>
            {settingsContent}
          </div>
        </div>
      </>
    );
  }

  // Otherwise, return as page component
  return <div className="w-full bg-[#f5f5f7] px-4 py-8 sm:px-6 lg:px-8">{settingsContent}</div>;
};
